/**
 * LiveRegion (§15.3, F148, M31): a ÚNICA região viva do app. Um `<p id="live-region" class="sr-only"
 * aria-live="polite" aria-atomic="true">`, renderizado pelo App uma vez, FORA de `#app-root` (que recebe `inert` com
 * diálogo aberto, §15.2.5) e dentro da RootErrorBoundary. Nenhum outro componente tem `aria-live` (AC-6.12-04).
 *
 * API única: `announce(text, kind)` (DE-15). `NoticeBar` → `'notice'` (aviso do feed) ou `'response'` (aviso
 * transitório, ex.: "Link copiado."); a busca → `'response'`. Eventos de saliência 3 e transições de conexão são
 * emitidos pela própria região a partir dos sinais `liveView` e `feed`.
 *
 * Regras (§15.3 bloco 5): só eventos novos (nunca os anteriores à montagem nem os já presentes na primeira vista);
 * `Set` de ids anunciados; no mesmo snapshot só o primeiro de tse_result (BR) > math_decision (BR) >
 * national_lead_change; ≥ 8 s entre anúncios de event/connection/notice (12 s na TV), com fila de até 2 (descarta a
 * mais antiga; prioridade connection > event > notice); `response` na hora, sem fila; com a busca aberta, só response
 * e connection; replay e aba oculta: nada é anunciado nem guardado; esvazia, escreve 100 ms depois e limpa 8 s
 * depois (12 s na TV); primeiro anúncio nunca antes de 1 s da montagem.
 */
import { effect } from '@preact/signals';
import { useEffect, useRef } from 'preact/hooks';
import { type CopyKey, t } from '../copy/strings';
import { fmtTimeAt } from '../format/time';
import { feed, liveView, now, timeline, ui } from '../state/store';

type View = NonNullable<typeof liveView.value>;
type LiveEvent = View['events'][number];
type Connection = (typeof feed.value)['connection'];

export type AnnounceKind = 'event' | 'connection' | 'notice' | 'response';

export interface LiveRegionProps {
  /**
   * Texto de leitor de tela de um evento de saliência 3: `text.screen_reader` ou o modelo de §13.5 para o `type`.
   * Padrão: só `text.screen_reader` (os modelos de §13.5 chegam com src/copy/events.ts, Fase 3, e o App os injeta).
   * `null` = não anuncia.
   */
  eventText?: (event: LiveEvent) => string | null;
  /**
   * Relógio da fonte em ms (o mesmo de `detected_at`; DE-B03): o App passa `() => source.clock()`. Padrão: `now`
   * quando já tiver tiques, senão `Date.now()`.
   */
  clock?: () => number;
}

/** Espaçamento e duração do texto (§15.3): 8 s, 12 s na TV. */
export const LIVE_SPACING_MS = 8000;
export const LIVE_SPACING_TV_MS = 12000;
/** Esvaziar e reescrever, para textos iguais serem relidos. */
export const LIVE_WRITE_DELAY_MS = 100;
/** Leitores de tela precisam registrar a região vazia antes do primeiro anúncio. */
export const LIVE_FIRST_DELAY_MS = 1000;
export const LIVE_QUEUE_MAX = 2;

const PRIORITY: Record<Exclude<AnnounceKind, 'response'>, number> = { connection: 3, event: 2, notice: 1 };

interface Item {
  text: string;
  kind: Exclude<AnnounceKind, 'response'>;
  /** Anúncio de perda (offline/stalled): só depois de dito vale o `live.online` (§15.3.1). */
  loss: boolean;
  seq: number;
}

/** Padrão de `eventText`: o texto de leitor de tela que veio no evento. */
export function defaultEventText(e: LiveEvent): string | null {
  const text = e.text?.screen_reader;
  return typeof text === 'string' && text.trim() !== '' ? text : null;
}

/** Ordem de §15.3: tse_result (BR) > math_decision (BR) > national_lead_change > demais. */
export function eventRank(e: Pick<LiveEvent, 'type' | 'scope'>): number {
  if (e.type === 'tse_result' && e.scope.level === 'BR') return 0;
  if (e.type === 'math_decision' && e.scope.level === 'BR') return 1;
  if (e.type === 'national_lead_change') return 2;
  return 3;
}

/**
 * {HHhMM} de §13.6.1 (M15): hora de dado em Brasília (UTC−3 fixo, §1.2) por fmtTimeAt (§13.4.3), com o dia depois da
 * meia-noite ("seg., 00h51"); nunca pelo fuso do aparelho.
 */
export function clockText(ms: number): string {
  return fmtTimeAt(ms);
}

/** `simulation` só substitui `live` (§10.6.1): para os anúncios, as duas valem como "ao vivo". */
function norm(c: Connection): Connection {
  return c === 'simulation' ? 'live' : c;
}

/**
 * "Sem números novos desde {HHhMM}" com a mesma hora do StatusIndicator (a do TSE do snapshot na tela, ou a da
 * publicação), e não a da última busca: com o feed parado, cada busca sem mudança renova `lastSuccessAt`, e a frase
 * diria "desde agora" (pré-mortem de 25/10, ORD-F-146; docs/DECISOES.md). Sem vista, o último `seq` novo aceito.
 */
function stalledSince(f: typeof feed.value): number | null {
  const v = liveView.peek();
  const iso = v?.tseHt ?? v?.publishedAt ?? null;
  const ms = iso ? Date.parse(iso) : Number.NaN;
  return Number.isFinite(ms) ? ms : (f.lastChangeAt ?? f.lastSuccessAt);
}

const LOSS_FROM: ReadonlySet<Connection> = new Set<Connection>(['offline', 'reconnecting', 'stalled']);

/** Controlador da região (um por documento). Exportado para os testes. */
export class LiveRegionController {
  private el: HTMLElement | null = null;
  private eventText: (e: LiveEvent) => string | null = defaultEventText;
  private clock: () => number = () => Date.now();
  private mountedAtData = 0;
  private mountedAtReal = 0;
  private baselineTaken = false;
  private readonly seen = new Set<string>();
  private queue: Item[] = [];
  private seq = 0;
  private lastSpokenAt: number | null = null;
  private lastNotice: string | null = null;
  private lossAnnounced = false;
  private prevConn: Connection = 'connecting';
  private writeTimer: ReturnType<typeof setTimeout> | null = null;
  private clearTimer: ReturnType<typeof setTimeout> | null = null;
  private drainTimer: ReturnType<typeof setTimeout> | null = null;
  private disposers: (() => void)[] = [];

  /** Liga a região ao elemento e aos sinais; devolve o desligamento. */
  attach(el: HTMLElement, opts: LiveRegionProps = {}): () => void {
    this.detach();
    this.el = el;
    this.eventText = opts.eventText ?? defaultEventText;
    this.clock = opts.clock ?? (() => now.peek().ms || Date.now());
    this.mountedAtData = this.clock();
    this.mountedAtReal = Date.now();
    this.baselineTaken = false;
    this.prevConn = norm(feed.peek().connection);
    el.textContent = '';
    this.disposers.push(effect(() => this.onFeed(feed.value)));
    this.disposers.push(effect(() => this.onView(liveView.value)));
    this.disposers.push(
      effect(() => {
        if (!timeline.value.live) this.queue = [];
      }),
    );
    this.disposers.push(
      effect(() => {
        if (!ui.value.searchOpen) this.pump();
      }),
    );
    const onVisibility = () => {
      if (document.hidden) this.queue = [];
    };
    document.addEventListener('visibilitychange', onVisibility);
    this.disposers.push(() => document.removeEventListener('visibilitychange', onVisibility));
    return () => this.detach();
  }

  detach(): void {
    for (const d of this.disposers.splice(0)) d();
    for (const timer of [this.writeTimer, this.clearTimer, this.drainTimer]) if (timer !== null) clearTimeout(timer);
    this.writeTimer = this.clearTimer = this.drainTimer = null;
    this.el = null;
    this.queue = [];
    this.seen.clear();
    this.lastSpokenAt = null;
    this.lastNotice = null;
    this.lossAnnounced = false;
  }

  /** §15.3 bloco 1. Sem região montada, não faz nada. */
  announce(text: string, kind: AnnounceKind): void {
    if (!this.el || typeof text !== 'string' || text.trim() === '') return;
    if (kind === 'response') {
      this.respond(text);
      return;
    }
    this.enqueue(text, kind, false);
  }

  private spacing(): number {
    return ui.peek().tv.on ? LIVE_SPACING_TV_MS : LIVE_SPACING_MS;
  }

  private respond(text: string): void {
    const wait = this.mountedAtReal + LIVE_FIRST_DELAY_MS - Date.now();
    if (wait > 0) {
      setTimeout(() => this.write(text), wait);
      return;
    }
    this.write(text);
  }

  private enqueue(text: string, kind: Item['kind'], loss: boolean): void {
    if (!timeline.peek().live) return;
    if (typeof document !== 'undefined' && document.hidden) return;
    if (kind === 'notice') {
      if (text === this.lastNotice) return;
      this.lastNotice = text;
    }
    this.queue.push({ text, kind, loss, seq: ++this.seq });
    while (this.queue.length > LIVE_QUEUE_MAX) this.queue.shift();
    this.pump();
  }

  /** Escreve o próximo da fila quando o espaçamento permite; senão agenda. */
  private pump(): void {
    if (!this.el) return;
    if (this.drainTimer !== null) {
      clearTimeout(this.drainTimer);
      this.drainTimer = null;
    }
    const searchOpen = ui.peek().searchOpen;
    let best: Item | null = null;
    for (const item of this.queue) {
      if (searchOpen && item.kind !== 'connection') continue;
      if (!best || PRIORITY[item.kind] > PRIORITY[best.kind]) best = item;
    }
    if (!best) return;
    const nowMs = Date.now();
    const ready = Math.max(
      this.mountedAtReal + LIVE_FIRST_DELAY_MS,
      this.lastSpokenAt === null ? 0 : this.lastSpokenAt + this.spacing(),
    );
    if (nowMs < ready) {
      this.drainTimer = setTimeout(() => this.pump(), ready - nowMs);
      return;
    }
    this.queue.splice(this.queue.indexOf(best), 1);
    this.lastSpokenAt = nowMs;
    if (best.loss) this.lossAnnounced = true;
    this.write(best.text);
    if (this.queue.length > 0) this.drainTimer = setTimeout(() => this.pump(), this.spacing());
  }

  /** Esvazia, escreve 100 ms depois e limpa 8 s (12 s na TV) depois. */
  private write(text: string): void {
    const el = this.el;
    if (!el) return;
    if (this.writeTimer !== null) clearTimeout(this.writeTimer);
    if (this.clearTimer !== null) clearTimeout(this.clearTimer);
    el.textContent = '';
    this.writeTimer = setTimeout(() => {
      this.writeTimer = null;
      if (this.el !== el) return;
      el.textContent = text;
      this.clearTimer = setTimeout(() => {
        this.clearTimer = null;
        if (this.el === el) el.textContent = '';
      }, this.spacing());
    }, LIVE_WRITE_DELAY_MS);
  }

  private say(key: CopyKey, lastSuccessAt: number | null, loss: boolean): void {
    if (lastSuccessAt === null) return; // sem números na tela, "Mostrando números de…" não se aplica
    let text: string;
    try {
      text = t(key, { HHhMM: clockText(lastSuccessAt) });
    } catch (err) {
      console.error('[live-region]', err);
      return;
    }
    this.enqueue(text, 'connection', loss);
  }

  /** Transições de §15.3.1. */
  private onFeed(f: typeof feed.value): void {
    const next = norm(f.connection);
    const prev = this.prevConn;
    this.prevConn = next;
    if (next === prev) return;
    if (next === 'offline') {
      this.say('live.offline', f.lastSuccessAt, true);
    } else if (next === 'stalled' && prev === 'live') {
      this.say('live.stalled', f.lastSuccessAt === null ? null : stalledSince(f), true);
    } else if (next === 'live' && LOSS_FROM.has(prev)) {
      this.queue = this.queue.filter((i) => !i.loss);
      if (this.lossAnnounced) this.say('live.online', f.lastSuccessAt, false);
    }
    if (next === 'live') this.lossAnnounced = false;
  }

  /**
   * Eventos de saliência 3 do snapshot aceito ao vivo (§15.3 bloco 3). A natureza `analysis` (o `insight_state`, único
   * tipo que a leva: I-19) nunca é anunciada, qualquer que seja a saliência: o cartão de situação incomum muda de
   * estado no painel, sem notificação (DR-33).
   */
  private onView(v: View | null): void {
    if (!v) return;
    const fresh: LiveEvent[] = [];
    for (const e of v.events) {
      if (e.salience !== 3 || e.nature === 'analysis' || this.seen.has(e.id)) continue;
      this.seen.add(e.id);
      fresh.push(e);
    }
    if (!this.baselineTaken) {
      // a primeira vista depois da montagem só registra: quem chega depois de uma virada a lê no herói (§15.3).
      this.baselineTaken = true;
      return;
    }
    const candidates = fresh.filter((e) => {
      const at = Date.parse(e.detected_at);
      return Number.isFinite(at) && at >= this.mountedAtData;
    });
    if (candidates.length === 0) return;
    candidates.sort((a, b) => eventRank(a) - eventRank(b));
    const top = candidates[0] as LiveEvent;
    let text: string | null;
    try {
      text = this.eventText(top);
    } catch (err) {
      console.error('[live-region]', err);
      return;
    }
    if (text) this.enqueue(text, 'event', false);
  }
}

const region = new LiveRegionController();

/** API única de anúncio (§15.3, DE-15). Sem a região montada, não faz nada. */
export function announce(text: string, kind: AnnounceKind): void {
  region.announce(text, kind);
}

/** A região viva única. O App a renderiza fora de `#app-root` (FE §5.2). */
export function LiveRegion({ eventText, clock }: LiveRegionProps) {
  const ref = useRef<HTMLParagraphElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    return region.attach(el, { eventText, clock });
  }, []);
  return (
    <p ref={ref} id="live-region" class="sr-only" aria-live="polite" aria-atomic="true" data-testid="live-region" />
  );
}
